import { useContext, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import * as turf from '@turf/turf';

import {
  DISTANCE_UNITS,
  feetPerMile,
  ftPerMeter,
  metersPerKm,
} from '../../../constants/units';
import { useUnits } from '../../../contexts/UnitContext';
import { MapContext } from '../../../contexts/MapContext';
import { resetMapLayers } from '../../../utils/map';
import { asRoundedDecimal } from '../../../utils/formatters';
import {
  addNonOverlappingLabels,
  groupSectionLabelsByCoordinate,
  removeOverlappingLabels,
} from '../../right-menu/export/ExportPdfButton';

const METRIC_SCALE_STEPS = [
  10 * ftPerMeter,
  100 * ftPerMeter,
  1000 * ftPerMeter,
  10_000 * ftPerMeter,
  100_000 * ftPerMeter,
  1_000_000 * ftPerMeter,
  Number.MAX_VALUE,
];

const IMPERIAL_SCALE_STEPS = [
  10,
  100,
  1000,
  feetPerMile,
  feetPerMile * 10,
  feetPerMile * 100,
  Number.MAX_VALUE,
];

// Converts `mapWidthFt` to a scale width and unit for the map
// NOTE: unitType is 'imperial' or 'metric'
const transformMapWidthToScaleWidth = (mapWidthFt, unitType) => {
  // We want the scale to be roughly 1/8 of the map's width
  const scaledDown = mapWidthFt / 8;

  // We'll round up to the next multiple of one of these steps:
  const steps =
    unitType === 'imperial' ? IMPERIAL_SCALE_STEPS : METRIC_SCALE_STEPS;

  // Find the index of the next step that is larger than the scaled down width
  const nextStepIndex = steps.findIndex((step) => step > scaledDown);
  let stepIndex = _.clamp(nextStepIndex, 1, steps.length - 1) - 1;

  // Edge case: if the value is between 5000 and 5280, we want to round it to 5280, not 6000
  if (
    unitType === 'imperial' &&
    scaledDown > 5000 &&
    scaledDown < feetPerMile
  ) {
    stepIndex = steps.indexOf(feetPerMile);
  }

  const stepSize = steps[stepIndex];

  const ft = Math.ceil(scaledDown / stepSize) * stepSize;
  const miles = ft / feetPerMile;

  const m = ft / ftPerMeter;
  const km = m / metersPerKm;

  // Pick a reasonable unit for display
  const unit =
    unitType === 'imperial'
      ? ft >= feetPerMile
        ? 'miles'
        : 'ft'
      : m >= metersPerKm
      ? 'km'
      : 'm';

  const width = {
    ft,
    miles,
    m,
    km,
  }[unit];

  // return the most reasonable unit for diplay & also return miles for the map geometry
  return { width, unit, miles };
};

const SCALE_LAYER = 'scale';
const SCALE_LABELS_LAYER = 'scale-labels';
const SCALE_LAYER_BACKGROUND = 'scale-background';

const toGeoJSON = (point) => turf.point([point.lng, point.lat]);

const getMapScaleFeatures = (map, { displayUnit }) => {
  const unitType = displayUnit === 'ft' ? 'imperial' : 'metric';

  const sw = toGeoJSON(map.getBounds().getSouthWest());
  const se = toGeoJSON(map.getBounds().getSouthEast());
  const mapWidthFt = turf.distance(sw, se, { units: 'feet' });

  const {
    width: scaleWidth,
    unit: scaleUnit,
    miles: scaleInMiles,
  } = transformMapWidthToScaleWidth(mapWidthFt, unitType);

  const scaleWidthFormatted = asRoundedDecimal(scaleWidth, { digits: 2 });

  const { offsetWidth: width, offsetHeight: height } = map.getContainer();

  // position the scale so that the end of the line is at the bottom right of the map
  const scaleEndXPixel = width - 65;
  const scaleEndYPixel = height - 50;
  const scaleEnd = turf.point(
    map.unproject([scaleEndXPixel, scaleEndYPixel]).toArray(),
  );

  // Here, we compute the scale based on actual distance. However, due to the
  // curvature of the earth and the projection of the map, this line may not be
  // fully horizontal (especially when the map is more zoomed out). To fix, we
  // ensure the y pixel value matches the scaleEnd.
  const scaleStartCoords = map.project(
    turf.destination(scaleEnd, scaleInMiles, -90, {
      units: 'miles',
    }).geometry.coordinates,
  );
  scaleStartCoords.y = scaleEndYPixel;
  const scaleStart = turf.point(
    map.unproject([scaleStartCoords.x, scaleStartCoords.y]).toArray(),
  );

  const scaleFeature = turf.lineString([
    scaleStart.geometry.coordinates,
    scaleEnd.geometry.coordinates,
  ]);

  // Hack to adjust the placement of the '0' label:
  const scaleLabelStart = turf.point(
    map.unproject([scaleStartCoords.x + 12, scaleStartCoords.y]).toArray(),
  );

  const labelData = turf.featureCollection([
    { ...scaleLabelStart, properties: { label: '0' } },
    {
      ...scaleEnd,
      properties: {
        label: `${scaleWidthFormatted}${
          scaleUnit === 'miles' ? ' ' : ''
        }${scaleUnit}`,
      },
    },
  ]);

  return { scaleFeature, labelData };
};

// Adds a fixed scale to the bottom right of the map
const addMapScale = (map, unit) => {
  const { scaleFeature, labelData } = getMapScaleFeatures(map, unit);

  map.addSource(SCALE_LAYER, {
    type: 'geojson',
    data: scaleFeature,
  });
  map.addSource(SCALE_LABELS_LAYER, {
    type: 'geojson',
    data: labelData,
  });
  map.addLayer({
    id: SCALE_LAYER_BACKGROUND,
    source: SCALE_LAYER,
    type: 'line',
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': '#000',
      'line-width': 8,
    },
  });
  map.addLayer({
    id: SCALE_LAYER,
    source: SCALE_LAYER,
    type: 'line',
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': '#fff',
      'line-width': 5,
    },
  });

  map.addLayer({
    id: SCALE_LABELS_LAYER,
    source: SCALE_LABELS_LAYER,
    type: 'symbol',
    layout: {
      'text-field': '{label}',
      'symbol-placement': 'point',
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
      'text-size': 12,
      'text-offset': [-1, 1],
    },
    paint: {
      'text-halo-color': 'rgba(0,0,0,1)',
      'text-halo-width': 2,
      'text-color': '#fff',
    },
  });
};

// Adjusts the scale in the map
const adjustMapScale = (map, unit) => {
  const { scaleFeature, labelData } = getMapScaleFeatures(map, unit);

  const scaleLayerSource = map.getSource(SCALE_LAYER);
  if (scaleLayerSource) scaleLayerSource.setData(scaleFeature);

  const scaleLabelsLayerSource = map.getSource(SCALE_LABELS_LAYER);
  if (scaleLabelsLayerSource) scaleLabelsLayerSource.setData(labelData);
};

// Removes the scale from the map (for unmount)
const removeMapScale = (map) => {
  resetMapLayers(map, [
    SCALE_LAYER,
    SCALE_LAYER_BACKGROUND,
    SCALE_LABELS_LAYER,
  ]);
};

/**
 * Returns the map center lng/lat as react state.
 */
export const useMapCenter = (mapLoaded, mapRef) => {
  const [mapCenter, setMapCenter] = useState([0, 0]);

  // Ensure we only add the handler once:
  const handlerHasBeenAdded = useRef(false);

  useEffect(() => {
    const map = mapRef.current;

    if (!map || !mapLoaded || handlerHasBeenAdded.current) return null;

    const updateCenter = () => {
      const center = map.getCenter();
      setMapCenter([center.lng, center.lat]);
    };

    map.on('zoom', updateCenter);
    map.on('move', updateCenter);

    // Set the initial lng/lat
    updateCenter();

    return () => {
      map.off('zoom', updateCenter);
      map.off('move', updateCenter);
    };
  }, [mapRef, mapLoaded]);

  return mapCenter;
};

/**
 * Adds and maintains a scale on the map.
 *
 * NOTE: this only works when the map's rotation and tilt are disabled.
 */
export const useMapScale = () => {
  const {
    mapState: { mapLoaded, mapRef },
  } = useContext(MapContext);

  // Ensure we only add the scale once:
  const scaleHasBeenAdded = useRef(false);

  const unit = useUnits(DISTANCE_UNITS);

  useEffect(() => {
    const map = mapRef.current;

    if (!map || !mapLoaded || scaleHasBeenAdded.current) return null;
    scaleHasBeenAdded.current = true;

    addMapScale(map, unit);

    const handler = () => {
      adjustMapScale(map, unit);
    };

    map.on('zoom', handler);
    map.on('move', handler);

    return () => {
      scaleHasBeenAdded.current = false;
      removeMapScale(map);

      map.off('zoom', handler);
      map.off('move', handler);
    };
  }, [mapRef, mapLoaded, unit]);
};

/**
 * Adds labels for the sections on the map.
 */
export const useMapSectionLabels = () => {
  const {
    mapState: { mapRef, mapLoaded },
    fields: { fieldSections },
  } = useContext(MapContext);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return null;

    const sectionLabels = groupSectionLabelsByCoordinate(
      fieldSections.filter((section) => section.shown),
    );

    addNonOverlappingLabels(map, sectionLabels);
    return () => {
      removeOverlappingLabels(map);
    };
  }, [mapLoaded, fieldSections, mapRef]);
};

export const useDisableMapRotation = () => {
  const {
    mapState: { mapRef, mapLoaded },
  } = useContext(MapContext);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return null;

    // Get initial settings
    const init = {
      dragRotateEnabled: map.dragRotate.isEnabled(),
      touchPitchEnabled: map.touchPitch.isEnabled(),
      touchRotationEnabled: !map.touchZoomRotate._rotationDisabled, // eslint-disable-line no-underscore-dangle
      keyboardRotationEnabled: !map.touchZoomRotate._rotationDisabled, // eslint-disable-line no-underscore-dangle
    };

    // Disable rotation (bearing) and tilt (pitch)
    map.dragRotate.disable();
    map.touchPitch.disable();
    map.touchZoomRotate.disableRotation();
    map.keyboard.disableRotation();

    // Reset them to 0
    map.setPitch(0);
    map.setBearing(0);

    // cleanup fn to reset the settings to init
    return () => {
      if (!map || map._removed) return; // eslint-disable-line no-underscore-dangle

      try {
        if (init.dragRotateEnabled) map.dragRotate.enable();
        if (init.touchPitchEnabled) map.touchPitch.enable();
        if (init.touchRotationEnabled) map.touchZoomRotate.enableRotation();
        if (init.keyboardRotationEnabled) map.keyboard.enableRotation();
      } catch (err) {
        // In case the map has unloaded or something, log errors but don't crash
        console.warn(err);
      }
    };
  }, [mapLoaded, mapRef]);
};
